import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import AvatarFallback from "../assets/avatar.jpg";
import StaffAttendanceService from "../services/StaffAttendanceService";

function roleLabel(role) {
  if (!role) return "Unknown";
  return role
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function roleBadgeClass(role) {
  switch (role) {
    case "teacher":
      return "bg-blue-50 text-blue-700";
    case "class teacher":
      return "bg-indigo-50 text-indigo-700";
    case "attender":
      return "bg-rose-50 text-rose-700";
    case "admin":
      return "bg-purple-50 text-purple-700";
    case "conductor":
      return "bg-emerald-50 text-emerald-700";
    case "parent":
      return "bg-amber-50 text-amber-700";
    case "student":
      return "bg-green-50 text-green-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

export default function StaffAttendanceManagement() {
  const navigate = useNavigate();
  const location = useLocation();

  const attendanceDate = useMemo(
    () => StaffAttendanceService.getTodayDateKey(),
    [],
  );
  const preset = useMemo(
    () => new URLSearchParams(location.search).get("preset"),
    [location.search],
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [rollSearch, setRollSearch] = useState("");
  const [presentByUser, setPresentByUser] = useState({});
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      setError("");
      setMessage("");

      try {
        const result = await StaffAttendanceService.getAttendanceDataByDate(
          attendanceDate,
        );
        setUsers(result.users);
        setRoleOptions(result.roleOptions);
        setPresentByUser(result.presentByUser);
      } catch (loadError) {
        console.error("Failed to load staff attendance data:", loadError);
        setError("Unable to load users right now. Please refresh and try again.");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [attendanceDate]);

  useEffect(() => {
    setSelectedRoles(
      StaffAttendanceService.getPresetRoles(preset, roleOptions),
    );
  }, [preset, roleOptions]);

  const filteredUsers = useMemo(() => {
    const normalizedQuery = rollSearch.trim().toLowerCase();

    return users.filter((user) => {
      const matchesRole =
        selectedRoles.length === 0 ||
        user.roles.some((role) => selectedRoles.includes(role));

      const rollNoText = String(user.rollNo || "").toLowerCase();
      const nameText = user.fullName.toLowerCase();
      const emailText = user.email.toLowerCase();

      const matchesSearch =
        !normalizedQuery ||
        rollNoText.includes(normalizedQuery) ||
        nameText.includes(normalizedQuery) ||
        emailText.includes(normalizedQuery);

      return matchesRole && matchesSearch;
    });
  }, [rollSearch, selectedRoles, users]);

  const presentCount = useMemo(
    () =>
      filteredUsers.reduce(
        (count, user) => count + (presentByUser[user.id] === true ? 1 : 0),
        0,
      ),
    [filteredUsers, presentByUser],
  );

  function cyclePresence(userId) {
    setPresentByUser((current) => ({
      ...current,
      [userId]:
        current[userId] === null
          ? true
          : current[userId] === true
            ? false
            : null,
    }));
  }

  function setVisiblePresence(value) {
    setPresentByUser((current) => {
      const updated = { ...current };
      filteredUsers.forEach((user) => (updated[user.id] = value));
      return updated;
    });
  }

  async function saveAttendance() {
    setSaving(true);
    setError("");
    setMessage("");

    try {
      await StaffAttendanceService.saveAttendance(
        attendanceDate,
        users,
        presentByUser,
      );

      setMessage("Attendance saved successfully.");
    } catch (saveError) {
      console.error("Failed to save staff attendance:", saveError);
      setError("Attendance could not be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4">
      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 p-5 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Attendance Management
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Date: {StaffAttendanceService.formatDateLabel(attendanceDate)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/manage-dashboard")}
              className="px-4 py-2 text-sm font-semibold border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              Back to Dashboard
            </button>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <FormControl size="small" fullWidth>
              <InputLabel id="roles-select-label">Filter Roles</InputLabel>
              <Select
                labelId="roles-select-label"
                multiple
                value={selectedRoles}
                onChange={(event) => {
                  const { value } = event.target;
                  setSelectedRoles(typeof value === "string" ? value.split(",") : value);
                }}
                input={<OutlinedInput label="Filter Roles" />}
                renderValue={(selected) =>
                  selected.length ? selected.map(roleLabel).join(", ") : "All Roles"
                }
              >
                {roleOptions.map((role) => (
                  <MenuItem key={role} value={role}>
                    <Checkbox checked={selectedRoles.includes(role)} />
                    <ListItemText primary={roleLabel(role)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <input
              type="text"
              value={rollSearch}
              onChange={(event) => setRollSearch(event.target.value)}
              placeholder="Filter by Roll No / Name / Email"
              className="w-full rounded-md border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <div className="rounded-md bg-blue-50 border border-blue-100 px-4 py-2.5">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                Visible Present
              </p>
              <p className="text-xl font-bold text-blue-900 mt-1">
                {presentCount} / {filteredUsers.length}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-5">
            <button
              type="button"
              onClick={() => setVisiblePresence(true)}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700 transition"
            >
              Mark Visible Present
            </button>
            <button
              type="button"
              onClick={() => setVisiblePresence(false)}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
            >
              Mark Visible Absent
            </button>
            <button
              type="button"
              onClick={() => setVisiblePresence(null)}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
            >
              Clear Visible Marks
            </button>
            <button
              type="button"
              onClick={saveAttendance}
              disabled={saving}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save Attendance"}
            </button>
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {message && (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {message}
            </div>
          )}

          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-5 py-3 text-left text-sm font-semibold text-gray-800">
                    Profile
                  </th>
                  <th className="px-5 py-3 text-left text-sm font-semibold text-gray-800">
                    Name
                  </th>
                  <th className="px-5 py-3 text-left text-sm font-semibold text-gray-800">
                    Roll No
                  </th>
                  <th className="px-5 py-3 text-left text-sm font-semibold text-gray-800">
                    Role
                  </th>
                  <th className="px-5 py-3 text-left text-sm font-semibold text-gray-800">
                    Present
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 bg-white">
                {!loading &&
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-5 py-4">
                        <img
                          src={user.profilePhoto || AvatarFallback}
                          alt={user.fullName}
                          className="w-12 h-12 rounded-full object-cover border border-gray-200"
                          onError={(event) => {
                            event.currentTarget.src = AvatarFallback;
                          }}
                        />
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-base font-semibold text-gray-900">
                          {user.fullName}
                        </p>
                        {user.email && (
                          <p className="text-sm text-gray-500">{user.email}</p>
                        )}
                      </td>
                      <td className="px-5 py-4 text-sm font-medium text-gray-700">
                        {user.rollNo}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-wrap gap-2">
                          {user.roles.length === 0 && (
                            <span className="px-2 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-600">
                              No Role
                            </span>
                          )}
                          {user.roles.map((role) => (
                            <span
                              key={`${user.id}-${role}`}
                              className={`px-2 py-1 rounded-md text-xs font-semibold ${roleBadgeClass(role)}`}
                            >
                              {roleLabel(role)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <Checkbox
                          checked={presentByUser[user.id] === true}
                          indeterminate={presentByUser[user.id] === null}
                          onClick={() => cyclePresence(user.id)}
                          sx={{ p: 0.5 }}
                        />
                      </td>
                    </tr>
                  ))}

                {loading && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-5 py-10 text-center text-gray-500 text-sm"
                    >
                      Loading users...
                    </td>
                  </tr>
                )}

                {!loading && filteredUsers.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-5 py-10 text-center text-gray-500 text-sm"
                    >
                      No users matched your current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
